import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useSubscribeApi,
  useSubscriptionPayApi,
} from '@hero/api-hooks';
import { getObjectId, type Id, type SubscriptionPublic } from '@hero/schema';
import { localizeMoyasarCheckoutUrl, type SubscriptionInterval } from '@hero/core';
import { useNav } from '../navigation';
import { buildBillingReturnUrl } from './buildBillingReturnUrl';

export interface UseSubscriptionPayManagerOptions {
  /**
   * Live subscription. `null` when the org has never had one — that's
   * the cold-signup branch that calls `subscribe`.
   */
  subscription: SubscriptionPublic | null;
  /**
   * Org id. Required for the no-subscription branch (`subscribe` needs
   * to know which org to attach the new subscription to). Resolved from
   * auth by the backend on `pay` calls.
   */
  orgId?: Id;
  /**
   * Optional notifier fired with the Moyasar hosted-checkout URL just
   * before this manager calls `window.location.assign`. Lets the caller
   * mount a "redirecting…" overlay or telemetry hook.
   */
  onCheckoutUrl?: (url: string) => void;
}

export interface PaySelection {
  planCode: string;
  interval: SubscriptionInterval;
  committedVehicleQty: number;
  /**
   * Committed orders per month. Sent alongside the seat count and
   * ignored by the backend unless the plan's basis is `per_order`.
   */
  committedOrderQty?: number;
}

/**
 * Single user-initiated entry point for paying a subscription —
 * whether that's a brand-new signup, a trial conversion, or a
 * mid-period upgrade.
 *
 * Behavior contract:
 *
 *   - No live subscription → `subscribe` (cold signup). Hosted checkout
 *     URL is returned; we navigate the browser to it.
 *   - Live subscription (any status) → `pay`. Backend decides whether
 *     to silently charge the saved token (`charged: true`, no URL) or
 *     hand off to hosted checkout (`checkoutUrl` set). Field-only
 *     changes (downgrade, no money owed) return `charged: false` with
 *     no URL — the manager navigates back to `billing` so the overview
 *     refetches and shows the new selection.
 *
 * Replaces the previous `useUpgradeFlowManager` (which incorrectly
 * routed mid-period changes through the field-only `change*`
 * endpoints, bypassing Moyasar entirely).
 */
export function useSubscriptionPayManager(options: UseSubscriptionPayManagerOptions) {
  const { subscription, orgId, onCheckoutUrl } = options;
  const subscribe = useSubscribeApi();
  const pay = useSubscriptionPayApi();
  const { navigate } = useNav();
  const { i18n } = useTranslation();

  const buildReturnUrls = useCallback(() => {
    // Both `success_url` and `callback_url` point at the dedicated
    // confirmation page — Moyasar appends `?id=<paymentId>&status=…`
    // itself, so the page can poll the BTx and render the right state
    // without us needing to thread our own flag through the URL.
    const base = buildBillingReturnUrl();
    return {
      successUrl: base,
      callbackUrl: base,
    };
  }, []);

  const handleCheckoutUrl = useCallback(
    (url: string | undefined) => {
      if (!url) return false;
      const localized = localizeMoyasarCheckoutUrl(url, i18n.language);
      onCheckoutUrl?.(localized);
      window.location.assign(localized);
      return true;
    },
    [onCheckoutUrl, i18n.language],
  );

  const handlePay = useCallback(
    async (selection: PaySelection) => {
      const { successUrl, callbackUrl } = buildReturnUrls();

      if (!subscription) {
        if (!orgId) return undefined;
        const result = await subscribe.mutateAsync({
          orgId: getObjectId(orgId),
          planCode: selection.planCode,
          interval: selection.interval,
          committedVehicleQty: selection.committedVehicleQty,
          committedOrderQty: selection.committedOrderQty,
          addons: [],
          callbackUrl,
          successUrl,
        });
        if (handleCheckoutUrl(result.checkoutUrl)) return result;
        // Free signup or already-saved-card cold path — no redirect.
        navigate('billing');
        return result;
      }

      const result = await pay.mutateAsync({
        planCode: selection.planCode,
        interval: selection.interval,
        committedVehicleQty: selection.committedVehicleQty,
        committedOrderQty: selection.committedOrderQty,
        callbackUrl,
        successUrl,
      });
      if (handleCheckoutUrl(result.checkoutUrl)) return result;
      // Either silent token charge succeeded or no money was owed.
      // Send the user back to overview so it can refetch and show the
      // new period / next renewal / saved card.
      navigate('billing');
      return result;
    },
    [subscription, orgId, subscribe, pay, buildReturnUrls, handleCheckoutUrl, navigate],
  );

  return {
    pay: handlePay,
    isSubmitting: subscribe.isPending || pay.isPending,
    subscribe,
    payApi: pay,
  };
}
